import { consoleWarningStyle } from '../constants';
import { getTerminalSessionId, isTokenExpired } from '../state';
import { errorHandler } from './errorHandler';
// Borrowed from https://github.com/kentcdodds/react-testing-library-course/blob/master/src/api.js
import { v4 as uuidv4 } from 'uuid';

async function client(
  endpoint,
  url,
  { data, token, headers: customHeaders, ...customConfig } = {},
) {
  if (!token) {
    console.warn(
      '%cWarning: Access token not passed.',
      `background-color: ${consoleWarningStyle}`,
    );
  }
  if (token && isTokenExpired()) {
    console.warn(
      '%cWarning: Access token expired.',
      `background-color: ${consoleWarningStyle}`,
    );
  }
  const loggingRequestId = uuidv4();
  const config = {
    method: data ? data.method || 'POST' : 'GET',
    body: data
      ? JSON.stringify({
          ...data,
          terminal_session_id:
            data?.terminal_session_id ?? getTerminalSessionId(),
        })
      : undefined,
    headers: {
      'X-Request-Id': loggingRequestId,
      Authorization: token ? `Bearer ${token}` : undefined,
      'Content-Type': data ? 'application/json' : 'application/json',
      ...customHeaders,
    },
    ...customConfig,
  };

  const path = url + endpoint;
  const response = await window.fetch(path, config);
  const responseData = await response.json();

  if (response.ok) {
    return responseData;
  } else {
    return errorHandler({
      code: response.status,
      status: response.statusText,
      message: responseData.message,
    });
  }
}

// Explicit endpoints
export const loginTerminal = ({ url, token, ...data }) =>
  client('/terminal_sessions', url, { token, data });
export const transferTerminal = ({ url, token, ...data }) =>
  client(`/terminal_sessions/${data.terminal_session_id}`, url, {
    token,
    data,
  });
export const fetchClientConfig = ({ url, token, ...data }) =>
  client(`/clients/${data.clientId}`, url, { token });
export const fetchCurrentAbilities = ({ url, token }) =>
  client('/current_abilities', url, { token });
export const fetchLocationsRequest = ({ url, token }) =>
  client(`/locations`, url, { token });
export const fetchTerminalsRequest = ({ url, token, ...data }) =>
  client(`/locations/${data.locationId}/terminals`, url, { token });
export const fetchProductsRequest = ({ url, token }) =>
  client(`/products?terminal_session_id=${getTerminalSessionId()}`, url, {
    token,
  });
export const fetchAgenciesRequest = ({ url, token }) =>
  client(`/agencies?terminal_session_id=${getTerminalSessionId()}`, url, {
    token,
  });
export const fetchLookupByIdRequest = ({ url, token, ...data }) =>
  client(
    `/products/${data.id}/lookup?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const voidOrderById = ({ url, token, ...data }) =>
  client(`/orders/${data.id}/void`, url, { token, data });
export const reopenOrderById = ({ url, token, ...data }) =>
  client(`/orders/${data.id}/reopen`, url, { token, data });
export const returnOrderById = ({ url, token, ...data }) =>
  client(`/orders/${data.id}/return`, url, { token, data });
export const makePayment = ({ url, token, ...data }) =>
  client(`/payments`, url, {
    token,
    data,
  });
export const getPaymentById = ({ url, token, ...data }) => {
  return client(`/payments/${data.id}`, url, { token });
};

export const pollByPaymentIdentifier = ({
  url,
  token,
  paymentAttemptUniqueId,
}) => {
  return client(
    `/payments/poll?payment_attempt_unique_identifier=${paymentAttemptUniqueId}`,
    url,
    { token },
  );
};

export const fetchServiceFeeRequest = ({ url, token, ...data }) =>
  client(
    `/payments/calculate_service_fee?amount=${
      data.amount
    }&terminal_session_id=${getTerminalSessionId()}`,
    url,
    {
      token,
    },
  );
export const fetchConvenienceFeeRequest = ({ url, token, ...data }) =>
  client(
    `/payments/convenience_fee?agency_id=${
      data.agency_id
    }&terminal_session_id=${getTerminalSessionId()}`,
    url,
    {
      token,
    },
  );
export const fetchSearchByIdRequest = ({ url, token, ...data }) =>
  client(
    `/products/${data.id}/search?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const addToCart = ({ url, token, ...data }) =>
  client(`/orders/update`, url, {
    token,
    data,
  });
export const getOrder = ({ url, token }) =>
  client(`/orders/current?terminal_session_id=${getTerminalSessionId()}`, url, {
    token,
  });
export const searchForItem = ({ url, token, ...data }) =>
  client(
    `/lookups?item_id=${data.id}&lookup_key=${data.key}&lookup_value=${
      data.value
    }&terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const cancelOrderById = ({ url, token, ...data }) =>
  client(`/orders/${data.id}/cancel`, url, { token, data });
export const holdOrder = ({ url, token }) =>
  client(`/orders/hold?terminal_session_id=${getTerminalSessionId()}`, url, {
    token,
  });
export const removeOrderItem = ({ url, token, ...data }) =>
  client(`/orders/remove_item`, url, { token, data });
export const fetchOrderDetails = ({ url, token, ...data }) =>
  client(
    `/orders/order_details?id=${
      data.id
    }&terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const postOrderReceipt = ({ url, token, ...data }) =>
  client(`/orders/${data.id}/receipt`, url, { token, data });
export const fetchOrders = ({ url, token, ...data }) =>
  client(
    `/orders?${data.filters}&page=${data.pageIndex}&per_page=${
      data.pageSize
    }&terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const fetchDrawerHistories = ({ url, token, ...data }) =>
  client(
    `/drawer_histories?${data.filters}&page=${data.pageIndex}&per_page=${
      data.pageSize
    }&terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );

export const fetchDrawerHistoryFilterOptions = ({ url, token, ...data }) =>
  client(
    `/drawer_histories/filter_options?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token, ...data },
  );

export const fetchOrdersFilterOptions = ({ url, token, ...data }) =>
  client(
    `/orders/filter_options?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token, ...data },
  );

export const fetchUsers = ({ url, token, ...data }) =>
  client(`/cashiers`, url, { token, ...data });
export const fetchBatches = ({ url, token, ...data }) =>
  client(`/terminal_sessions`, url, { token, ...data });
export const openCashDrawer = ({ url, token }) =>
  client(
    `/peripherals/open_cash_drawer?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const createCloseout = ({ url, token, ...data }) =>
  client('/batches/current/close', url, { token, data });
export const fetchCloseoutReport = ({ url, token }) =>
  client(
    `/batches/report/current?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const reprintCloseoutReports = ({ url, token, ...data }) =>
  client(`/batches/${data.id}/report`, url, { token });
export const fetchCurrentBatch = ({ url, token }) =>
  client(
    `/batches/current?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );
export const postCashDrop = ({ url, token, ...data }) =>
  client(`/terminal_sessions/cash_actions`, url, { token, ...data });
export const fetchCashAction = ({ url, token, ...data }) =>
  client(`/cash_actions/${data.id}`, url, { token });
export const fetchCloseoutDetails = ({ url, token, ...data }) =>
  client(`/batches/${data.id}/closeout_details`, url, { token });
export const cancelTransaction = ({ url, token, ...data }) =>
  client(`/peripherals/cancel`, url, { token, ...data });
export const cashActionReceipt = ({ url, token, ...data }) =>
  client(`/cash_actions/${data.id}/receipt`, url, {
    token,
  });

export const fetchDenominations = ({ url, token }) =>
  client(`/denominations`, url, {
    token,
  });
export const voidTransactionById = ({ url, token, ...data }) =>
  client(`/payments/${data.id}/void`, url, { token, data });
export const fetchPeriscopeDashboardIFrameUrl = ({ url, token }) =>
  client(
    `/periscope_dashboard?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token },
  );

export const cancelPaylinkById = ({ url, token, ...data }) =>
  client(`/payments/paylink/${data.id}/cancel`, url, { token, data });

/**
 * After any Sale transaction,
 * we must send this request for the user to "Remove Card". If the card is already removed, we
 * still receive a "Success" message. (https://www.dsidevportal.com/api/trancloudow-trancloud-works)
 * we should perform this after each transaction attempt, regardless of success or failure
 * This is required before any subsequent transaction attempt.
 */

export const resetPinPad = ({ url, token, ...data }) =>
  client(
    `/terminals/${
      data.terminalId
    }/reset_pin_pad?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token, ...data },
  );

// Find or create a visit in Ghenghis, then cache it in the Redux store.
export const postVisitId = ({ url, token, order_id, ...data }) =>
  client(
    `/orders/${order_id}/visits?terminal_session_id=${getTerminalSessionId()}`,
    url,
    { token, data },
  );
